import type * as React from "react";
import { cn } from "cn";

/**
 * One key of a keyboard shortcut, drawn as a small keycap: a hairline, a faint fill and a mono
 * label, or an icon (give an icon a `sr-only` name beside it, since an icon says nothing to a
 * screen reader). It shows a shortcut; it does not listen for one, so the page wires the keys
 * itself. It takes no pointer events, so it can sit inside a button without getting in its way.
 *
 * Name keys as the person's keyboard does: `esc`, `⌘`, `K`. A shortcut that is several keys held
 * together is a {@link KbdGroup} of `Kbd`s.
 */
function Kbd({ className, ...props }: React.ComponentProps<"kbd">) {
  return (
    <kbd
      data-slot="kbd"
      className={cn(
        "pointer-events-none inline-flex h-5 min-w-5 items-center justify-center gap-1 rounded-[5px] border border-border bg-band px-1.5 font-mono text-[10.5px]/3.5 font-medium text-muted-foreground select-none [&_svg]:size-3 [&_svg]:shrink-0",
        className,
      )}
      {...props}
    />
  );
}

/** The keys of one shortcut side by side, with room between the caps. */
function KbdGroup({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="kbd-group"
      className={cn("inline-flex items-center gap-1", className)}
      {...props}
    />
  );
}

export { Kbd, KbdGroup };
